import React from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Typography,
    Box,
    Divider,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";

/**
 * LsDialog — general-purpose dialog wrapper
 *
 * Props:
 * open       boolean
 * onClose    () => void
 * title      string | ReactNode
 * subtitle   string | ReactNode   (optional)
 * actions    ReactNode            (optional — renders footer action bar)
 * maxWidth   "xs"|"sm"|"md"|"lg" (default: "sm")
 * fullWidth  boolean              (default: true)
 */
const LsDialog = ({
                      open,
                      onClose,
                      title,
                      subtitle,
                      children,
                      actions,
                      maxWidth = "sm",
                      fullWidth = true,
                      ...props
                  }) => {
    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth={maxWidth}
            fullWidth={fullWidth}
            PaperProps={{
                sx: {
                    borderRadius: 2,
                    boxShadow: "0px 8px 24px rgba(0,0,0,0.12)",
                    minWidth: 400,
                },
            }}
            {...props}
        >
            {/* ── Title ── */}
            <DialogTitle
                sx={{
                    px: 2.5,
                    pt: 2,
                    pb: 1.5,
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    gap: 1,
                }}
            >
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.4 }}>
                    <Typography
                        component="span"
                        sx={{ fontSize: "0.9375rem", fontWeight: 600, color: "text.primary", lineHeight: 1.3 }}
                    >
                        {title}
                    </Typography>
                    {subtitle && (
                        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                            {subtitle}
                        </Typography>
                    )}
                </Box>

                {onClose && (
                    <IconButton
                        aria-label="Close dialog"
                        onClick={onClose}
                        size="small"
                        sx={{
                            color: "text.secondary",
                            mt: -0.25,
                            flexShrink: 0,
                            "&:hover": { bgcolor: "action.hover" },
                        }}
                    >
                        <CloseIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                )}
            </DialogTitle>

            <Divider />

            {/* ── Body ── */}
            <DialogContent sx={{ px: 2.5, py: 2.5 }}>
                {children}
            </DialogContent>

            {/* ── Actions (optional) ── */}
            {actions && (
                <>
                    <Divider />
                    <DialogActions
                        sx={{
                            px: 2.5,
                            py: 1.5,
                            gap: 1,
                            bgcolor: "background.default",
                        }}
                    >
                        {actions}
                    </DialogActions>
                </>
            )}
        </Dialog>
    );
};

export default LsDialog;